import { Grid, Checkbox, TextField, FormControlLabel } from '@material-ui/core';
import React, { useState, useEffect, useMemo, useContext } from 'react';
import styled from 'styled-components';
import { color } from 'styles/utils';
import useInput from 'lib/hooks/useInput';
import useCheckSetInput from 'lib/hooks/useCheckSetInput';
import { PartnerActions, AppActions } from 'store/actionCreators';
import MuiButton from 'components/common/button/MuiButton';
import MuiWrapper from 'components/common/input/MuiWrapper';
import T from 'components/common/text/T';
import { icon_user_circle, icon_magnifier, hand_shake } from 'components/base/images';
import CustomSpan from 'components/common/text/CustomSpan';
import PartnerRequestSlick from 'components/partner/PartnerRequestSlick';
import NewPartnerModalContainer from 'containers/partner/NewPartnerModalContainer';
import UnitPriceModalContainer from 'containers/account/UnitPriceModalContainer';
import GoldStatusModalContainer from 'containers/project/GoldStatusModalContainer';
import { useTranslation } from 'react-i18next';
import AppModal from 'components/common/modal/AppModal';
import PlainModal from 'components/common/modal/PlainModal';
import DateConverter from 'components/common/convert/DateConverter';
import { partnerTypeList } from 'lib/mapper';
import { useShallowAppSelector } from 'store/hooks';
import { useDidUpdateEffect } from 'lib/utils';
import InfiniteScroll from 'react-infinite-scroll-component';
import CircularLoading from 'components/base/loading/CircularLoading';
import AlertModalContent from 'components/common/modal/contents/AlertModalContent';
import ImgCrop from 'components/common/images/ImgCrop';
import { OffsetPagingData, ILoginUser } from 'lib/types/sharedTypes';
import { PrivateSocketContext } from 'contexts/PrivateSocketContext';
import PageTitle from 'components/base/title/PageTitle';
import { PageSubTitle } from 'components/base/title/PageTitle';
import { validInputString } from 'lib/library';
import ListEmpty from 'components/common/list/ListEmpty';

interface IPartner {
  partnershipIdx: number;
  partnershipCode: string;
  accountType: number;
  userGroupIdx: number;
  groupCode: string;
  email: string | null;
  profileImg: string | null;
  name: string | null;
  userTypeIdx: number | null;
  modifyDate: number | null;
}

interface IReceivedPartner {
  partnershipIdx: number;
  state: number;
  userGroupIdx: number;
  groupCode: string;
  email: string;
  name: string;
  profileImg: string | null;
  enrollDate: number;
}

interface IRequestPartner {
  partnershipIdx: number;
  partnershipCode: string;
  email: string;
  name: string;
  userTypeIdx: number;
  enrollDate: number;
}

function PartnerContainer() {
  const {
    language,
    user,
    fetchPartnerList: { list: partners, pagingData: partnerListPagingData },
    fetchPartnerListSuccess,
    fetchPartnerListPending,
    fetchPartnerListFailure,
    fetchPartnerRequestList: { list: partnerRequestList },
    fetchPartnerRequestListSuccess,
    fetchPartnerRequestListPending,
    fetchPartnerRequestListFailure,
    fetchPartnerReceiveRequested: { list: receiveRequested },
    fetchPartnerReceiveRequestedSuccess,
    fetchPartnerReceiveRequestedPending,
    fetchPartnerReceiveRequestedFailure,
    answerReceiveRequestedSuccess,
    answerReceiveRequestedFailure,
    cancelRequestPartnerSuccess,
  }: {
    language: string;
    user: ILoginUser;
    fetchPartnerList: {
      list: IPartner[];
      pagingData: OffsetPagingData;
    };
    fetchPartnerListSuccess: boolean;
    fetchPartnerListPending: boolean;
    fetchPartnerListFailure: boolean;
    fetchPartnerRequestList: {
      list: IRequestPartner[];
    };
    fetchPartnerRequestListSuccess: boolean;
    fetchPartnerRequestListPending: boolean;
    fetchPartnerRequestListFailure: boolean;
    fetchPartnerReceiveRequested: {
      list: IReceivedPartner[];
    };
    fetchPartnerReceiveRequestedSuccess: boolean;
    fetchPartnerReceiveRequestedPending: boolean;
    fetchPartnerReceiveRequestedFailure: boolean;
    answerReceiveRequestedSuccess: boolean;
    answerReceiveRequestedFailure: boolean;
    cancelRequestPartnerSuccess: boolean;
  } = useShallowAppSelector(state => ({
    language: state.base.language,
    user: state.user.user || {},
    fetchPartnerList: state.partner.partnerList.data || { list: {}, pagingData: {} },
    fetchPartnerListSuccess: state.partner.partnerList.success,
    fetchPartnerListPending: state.partner.partnerList.pending,
    fetchPartnerListFailure: state.partner.partnerList.failure,
    fetchPartnerRequestList: state.partner.partnerRequestList.data || {},
    fetchPartnerRequestListSuccess: state.partner.partnerRequestList.success,
    fetchPartnerRequestListPending: state.partner.partnerRequestList.pending,
    fetchPartnerRequestListFailure: state.partner.partnerRequestList.failuer,
    fetchPartnerReceiveRequested: state.partner.partnerReceiveRequestedList.data || {},
    fetchPartnerReceiveRequestedSuccess: state.partner.partnerReceiveRequestedList.success,
    fetchPartnerReceiveRequestedPending: state.partner.partnerReceiveRequestedList.pending,
    fetchPartnerReceiveRequestedFailure: state.partner.partnerReceiveRequestedList.failure,
    answerReceiveRequestedSuccess: state.partner.answerReceiveRequested.success,
    answerReceiveRequestedFailure: state.partner.answerReceiveRequested.failure,
    cancelRequestPartnerSuccess: state.partner.cancelRequestPartner.success,
  }));
  const [isLoadSuccess, setIsLoadSuccess] = useState<boolean>(false);
  const [isLoadFailure, setIsLoadFailure] = useState<boolean>(false);
  const [isLoadRequestSuccess, setIsLoadRequestSuccess] = useState<boolean>(false);
  const [isLoadRequestFailure, setIsLoadRequestFailure] = useState<boolean>(false);
  const [isLoadReceivedSuccess, setIsLoadReceivedSuccess] = useState<boolean>(false);
  const [isLoadReceivedFailure, setIsLoadReceivedFailure] = useState<boolean>(false);
  const { ipcRenderer } = window;
  const { t } = useTranslation();
  const { newPartnerListStack } = useContext(PrivateSocketContext);
  const hasMorePartnerList = partnerListPagingData?.page < partnerListPagingData?.totalPage;
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalType, setModalType] = useState<string>('');
  const checkPartner = useCheckSetInput(new Set([]));
  const checkPartnerType = useCheckSetInput(new Set([]));
  const checkAll = useInput(false);
  const receivedRequestGroupCode = useInput('');
  const [prevPartnerStateProp, setPrevPartnerStateProp] = useState<number | null>(null);
  const detailPartnerGroupIdx = useInput('');
  const detailPartnerGroupCode = useInput('');
  const detailPartnerShipIdx = useInput('');
  const detailPartnerShipCode = useInput('');
  const detailPartnerShipName = useInput('');
  const detailPartnerShipEmail = useInput('');
  const keyword = useInput('');
  const page = useInput(1);
  const [partnerListStack, setPartnerListStack] = useState<any[]>([]);
  const [newPartnerList, setNewPartnerList] = useState<any[]>([]);
  const [partnerRequestStack, setPartnerRequestStack] = useState<any[]>([]);
  const isModalModify = useInput(false);
  const [isModalClose, setIsModalClose] = useState<boolean>(false);
  const [isDashboard, setIsDashboard] = useState<boolean>(false);
  const majorClassify = useInput('');
  const minorClassify = useInput('');
  const updateSucessType = useInput(''); //상태변화 상태 - new, modify(information, profileImage), delete
  const updateUserData = useInput(null); //상태변화 값
  const nonvalidatedNotice = {
    title: t('GLOBAL_PARTNER_REQUEST'),
    mainText: t('INVALID_PARTNER_REQUEST'),
    subText: '',
  };

  const alertNonvalidateRequestPartner = () => {
    AppActions.add_popup({
      isOpen: true,
      isCloseIcon: true,
      width: 660,
      title: '',
      content: (
        <AlertModalContent
          title={nonvalidatedNotice.title}
          mainText={nonvalidatedNotice.mainText}
          subText={nonvalidatedNotice.subText}
        />
      ),
      onClick() {
        return;
      },
      onCancel() {
        return;
      },
      onExit() {
        PartnerActions.set_invalid_parter_groupcode(receivedRequestGroupCode.value);
        return;
      },
    });
  };

  let submitPartnerListParams = useMemo(() => {
    let partnerType = [];
    if (checkPartnerType.value.has(2)) {
      partnerType = [...checkPartnerType.value, 3];
    } else {
      partnerType = [...checkPartnerType.value];
    }
    return {
      keyword: validInputString(keyword.value),
      page: page.value,
      userTypeIdx: partnerType.join('%'),
    };
  }, [page.value, checkPartnerType.value, keyword.value]);

  useDidUpdateEffect(() => {
    if (!cancelRequestPartnerSuccess) {
      setIsLoadRequestSuccess(false);
      setIsLoadRequestFailure(false);
      PartnerActions.fetch_partner_request_list_request();
      checkAll.setValue(false);
      checkPartner.setValue(new Set([]));
    }
  }, [cancelRequestPartnerSuccess]);

  useEffect(() => {
    //init
    PartnerActions.fetch_partner_list_request(submitPartnerListParams);
    PartnerActions.fetch_partner_request_list_request();
    PartnerActions.fetch_partner_receive_requested_list_request();

    return () => {
      setIsLoadSuccess(false);
      setIsLoadFailure(false);
      setIsLoadRequestSuccess(false);
      setIsLoadRequestFailure(false);
      setIsLoadReceivedSuccess(false);
      setIsLoadReceivedFailure(false);
    };
  }, []);

  useDidUpdateEffect(() => {
    if (!!updateUserData.value) {
      if (updateSucessType.value === 'edit') {
        const changePartnerArray: any[] = partnerListStack.map(item => {
          return item.groupCode === updateUserData.value.groupCode ? updateUserData.value : item;
        });

        const newPartnerArray: any[] = newPartnerList.map(item => {
          return item.groupCode === updateUserData.value.groupCode ? updateUserData.value : item;
        });
        setPartnerListStack([...changePartnerArray]);
        setNewPartnerList([...newPartnerArray]);
        updateSucessType.setValue('');
        updateUserData.setValue(null);
      }
      if (updateSucessType.value === 'new') {
        // newPartnerList
        const tempArray: any[] = newPartnerList.concat(updateUserData.value);
        setNewPartnerList([...tempArray]);
        updateSucessType.setValue('');
        updateUserData.setValue(null);
      }
      if (updateSucessType.value === 'delete') {
        const changePartnerArray: any[] = partnerListStack.filter(item => {
          return item.groupCode !== updateUserData.value.groupCode;
        });

        const newPartnerArray: any[] = newPartnerList.filter(item => {
          return item.groupCode !== updateUserData.value.groupCode;
        });
        setPartnerListStack([...changePartnerArray]);
        setNewPartnerList([...newPartnerArray]);
        updateSucessType.setValue('');
        updateUserData.setValue(null);
      }
      if (updateSucessType.value === 'request') {
        setIsLoadRequestSuccess(false);
        setIsLoadRequestFailure(false);
        PartnerActions.fetch_partner_request_list_request();
        updateSucessType.setValue('');
        updateUserData.setValue(null);
      }
    }
  }, [updateSucessType.value, updateUserData.value]);

  const handleRefreshList = () => {
    PartnerActions.fetch_partner_list_request(submitPartnerListParams);
    setIsLoadSuccess(false);
    setIsLoadFailure(false);
  };

  const handleRefreshListRequset = () => {
    PartnerActions.fetch_partner_request_list_request();
    setIsLoadRequestSuccess(false);
    setIsLoadRequestFailure(false);
  };

  const handleRefreshListReceived = () => {
    PartnerActions.fetch_partner_receive_requested_list_request();
    setIsLoadReceivedSuccess(false);
    setIsLoadReceivedFailure(false);
  };

  const handleCheckCloseModal = () => {
    if (!isModalModify.value) {
      handleClosePartnerModal();
    } else {
      setIsModalClose(true);
      setIsModalOpen(true);
    }
  };

  const handleSearchPartner = () => {
    setPartnerListStack([]);
    page.setValue(1);
    let partnerType = [];
    if (checkPartnerType.value.has(2)) {
      partnerType = [...checkPartnerType.value, 3];
    } else {
      partnerType = [...checkPartnerType.value];
    }
    const checkText = validInputString(keyword.value);
    keyword.setValue(checkText);
    const submitData = {
      keyword: checkText,
      page: 1,
      userTypeIdx: partnerType.join('%'),
    };
    setIsLoadFailure(false);
    setIsLoadSuccess(false);
    PartnerActions.fetch_partner_list_request(submitData);
  };

  const handleCheckPartnerType = (index: number) => {
    checkPartnerType.onChange({ value: index });
    handleSearchPartner();
  };

  const handleOpenModalPopup = (type: string | any) => {
    // type :: 0 => UnitPrice , 1 => Employee, 2 => Product
    setModalType(type);
    setIsModalOpen(true);
  };

  const handleCheckAll = (isCheckAll: boolean) => {
    checkPartner.setValue(new Set([]));
    if (isCheckAll) {
      partnerRequestList.forEach((item: any) => {
        checkPartner.onChange({ value: item.partnershipCode });
      });
    }
    checkAll.setValue(!isCheckAll);
  };

  const handleCheckStandByPartner = (partnershipCode: string | any) => {
    checkPartner.onChange({ value: partnershipCode });
  };

  useDidUpdateEffect(() => {
    if (partnerRequestList.length === checkPartner.value.size) {
      checkAll.setValue(true);
    } else {
      checkAll.setValue(false);
    }
  }, [checkPartner.value, checkAll.value]);

  const handleFetchParterList = () => {
    const submitData = {
      keyword: validInputString(keyword.value),
      page: page.value + 1,
      userTypeIdx: [...checkPartnerType.value]?.join('%'),
    };
    PartnerActions.fetch_partner_list_request(submitData);
    page.setValue((draft: number) => draft + 1);
  };

  const handleClosePartnerModal = () => {
    receivedRequestGroupCode.setValue('');
    setIsModalOpen(false);
    setIsModalClose(false);
    isModalModify.setValue(false);
    majorClassify.setValue('');
    minorClassify.setValue('');
    setPrevPartnerStateProp(null);
  };

  const handleClickReceiveRequest = (partner: any, answer: any) => {
    receivedRequestGroupCode.setValue(partner.groupCode);
    if (!!answer) {
      // 수락
      setPrevPartnerStateProp(3);
      handleOpenModalPopup('newPartner');
      detailPartnerGroupCode.setValue('');
    } else {
      // 거절
      const submitData = {
        partnershipIdx: partner.partnershipIdx,
        reqGroupCode: partner.groupCode,
        reqEmail: partner.email,
        state: 2,
        userTypeIdx: partner.value,
        currency: user?.currencyCode,
      };
      setIsDashboard(true);
      PartnerActions.answer_receive_requested_request(submitData);
    }
  };

  const handleCancelRequestPartnerShip = () => {
    const submitData = {
      partnershipCodeArr: [...checkPartner.value],
    };
    PartnerActions.cancel_request_partnership_request(submitData);
  };

  useDidUpdateEffect(() => {
    if (answerReceiveRequestedSuccess) {
      setIsLoadReceivedSuccess(false);
      setIsLoadReceivedFailure(false);
      PartnerActions.fetch_partner_receive_requested_list_request();
      if (ipcRenderer) {
        ipcRenderer.send('setPartnerList', {});
      }
    }
  }, [!!answerReceiveRequestedSuccess]);

  useDidUpdateEffect(() => {
    if (answerReceiveRequestedFailure && isDashboard) {
      alertNonvalidateRequestPartner();
      setIsLoadReceivedSuccess(false);
      setIsLoadReceivedFailure(false);
      PartnerActions.fetch_partner_receive_requested_list_request();
      setIsDashboard(false);
    }
  }, [answerReceiveRequestedFailure, isDashboard]);

  useDidUpdateEffect(() => {
    if (!fetchPartnerListSuccess) return;

    if (page.value === 1) {
      setPartnerListStack(partners);
      setIsLoadSuccess(true);
    } else {
      setPartnerListStack(draft => [...draft, ...partners]);
    }
  }, [fetchPartnerListSuccess]);

  useDidUpdateEffect(() => {
    if (!fetchPartnerListFailure) return;
    setIsLoadFailure(true);
  }, [fetchPartnerListFailure]);

  useDidUpdateEffect(() => {
    if (!fetchPartnerRequestListSuccess) {
      setIsLoadRequestSuccess(true);
      setPartnerRequestStack(partnerRequestList);
      if (ipcRenderer) {
        ipcRenderer.send('setPartnerList', {});
      }
    }
  }, [fetchPartnerRequestListSuccess]);

  useDidUpdateEffect(() => {
    if (!fetchPartnerRequestListFailure) return;
    setIsLoadRequestFailure(true);
  }, [fetchPartnerRequestListFailure]);

  useDidUpdateEffect(() => {
    if (!fetchPartnerReceiveRequestedSuccess) return;
    setIsLoadReceivedSuccess(true);
  }, [fetchPartnerReceiveRequestedSuccess]);

  useDidUpdateEffect(() => {
    if (!fetchPartnerReceiveRequestedFailure) return;
    setIsLoadReceivedFailure(true);
  }, [fetchPartnerReceiveRequestedFailure]);

  useDidUpdateEffect(() => {
    newPartnerListStack.forEach((item: any) => {
      if (checkPartner.value.has(item.partnershipCode)) {
        checkPartner.onChange({ value: item.partnershipCode });
      }
    });
    // state에서 제거하기엔 이중 반복문이라 새로 불러옴
    setIsLoadRequestSuccess(false);
    setIsLoadRequestFailure(false);
    PartnerActions.fetch_partner_request_list_request();
  }, [newPartnerListStack]);

  useDidUpdateEffect(() => {
    if (newPartnerListStack.length > 0) {
      const tempNewParter = newPartnerListStack.filter((partner: any, idx: number, arr: any[]) => {
        return arr.findIndex((item: any) => item.groupCode === partner.groupCode) === idx;
      });
      setNewPartnerList(tempNewParter);
    }
  }, [newPartnerListStack]);

  const modalList = [
    {
      type: 'unitPrice',
      title: t('GLOBAL_UNIT_PRICE'),
      component: (
        <UnitPriceModalContainer
          modifyUnitPriceKeyCode={''}
          partnershipIdx={detailPartnerShipIdx.value}
          partnershipName={detailPartnerShipName.value}
          partnershipEmail={detailPartnerShipEmail.value}
          isModalModify={isModalModify}
          isModalClose={isModalClose}
          onCloseModal={handleClosePartnerModal}
        />
      ),
      width: 1160,
      padding: '0 0 28px',
      backgroundColor: 'transparent',
    },
    {
      type: 'newPartner',

      title: t('GLOBAL_NEW_PARTNER'),
      component: (
        <NewPartnerModalContainer
          major={majorClassify}
          minor={minorClassify}
          prevPartnerStateProp={prevPartnerStateProp}
          detailPartnerGroupCode={detailPartnerGroupCode}
          receivedRequestGroupCode={receivedRequestGroupCode.value}
          updateSucessType={updateSucessType}
          updateUserData={updateUserData}
          onClosePartnerModal={handleClosePartnerModal}
        />
      ),
      width: 'auto',
      padding: 0,
      backgroundColor: 'transparent',
    },
    {
      type: 'goldStatus',
      title: t('GLOBAL_GOLD_STATUS'),
      component: (
        <GoldStatusModalContainer
          partnershipIdx={detailPartnerShipIdx.value}
          partnershipCode={detailPartnerShipCode.value}
          partnerGroupCode={detailPartnerGroupCode.value}
          partnerGroupIdx={detailPartnerGroupIdx.value}
          onClose={handleClosePartnerModal}
        />
      ),
      width: 1280,
      padding: '30px 35px 25px',
      borderRadius: '8px',
      headerCenterText: {
        text: 'Dof_project_123(project name)',
        width: 325,
      },
    },
  ];

  return (
    <StyledPartner data-component-name="Partner">
      {useMemo(() => {
        const findData = modalList.find(item => item.type === modalType);
        return (
          <PlainModal isOpen={isModalOpen} onClose={handleCheckCloseModal} width={findData?.width}>
            <AppModal
              title={findData?.title}
              content={findData?.component}
              headerCenterText={findData?.headerCenterText?.text}
              headerCenterTextStyle={{ width: findData?.headerCenterText?.width }}
              contentCardStyle={{
                padding: findData?.padding,
                borderRadius: findData?.borderRadius,
                backgroundColor: findData?.backgroundColor,
              }}
              isCloseIcon={true}
              onClick={handleCheckCloseModal}
              onCancel={handleCheckCloseModal}
              hideButton={true}
            />
          </PlainModal>
        );
      }, [isModalOpen, modalType, isModalClose, isModalModify.value])}
      <PageTitle title={t('NAV_MENU_PARTNER')} />
      <Grid container className="partner__container">
        <Grid item>
          <PageSubTitle title={t('PARTNER_RECEIVED_REQUEST')} />
          <section className="partner__received_list_wrapper">
            {!isLoadReceivedSuccess && !!fetchPartnerReceiveRequestedPending && (
              <div className="infinite-scroll-loading">
                <CircularLoading />
              </div>
            )}
            {useMemo(() => {
              const exeptSignOutReq = receiveRequested?.filter(item => {
                return !!item.groupCode;
              });

              return (
                <>
                  {!!isLoadReceivedSuccess &&
                    (exeptSignOutReq?.length > 0 ? (
                      <>
                        <CustomSpan fontSize={12} fontWeight={400}>
                          <T>PARTNER_REQUEST_ACCEPTION</T>
                        </CustomSpan>
                        <div className="partner__request_list_wrapper">
                          <PartnerRequestSlick
                            receiveRequest={exeptSignOutReq}
                            onClickPartnerRequest={handleClickReceiveRequest}
                          />
                        </div>
                      </>
                    ) : (
                      <div className="received_partner_is_empty_wrapper">
                        <div className="received_partner_is_empty_image">
                          <img src={hand_shake} />
                        </div>
                        <div className="received_partner_is_empty_text">
                          <T>PARTNER_REQUEST_EMPTY</T>
                        </div>
                      </div>
                    ))}
                </>
              );
            }, [receiveRequested, isLoadReceivedSuccess])}
            {!!isLoadReceivedFailure && (
              <ListEmpty
                text="GLOBAL_LIST_ERROR"
                isRefreshButton={true}
                onRefresh={handleRefreshListReceived}
              />
            )}
          </section>
        </Grid>
        <Grid item className="partner__contacts_wrapper">
          <div className="partner__contacts_header">
            <PageSubTitle title={t('PARTNER_SENT_REQUEST')} />

            <div className="partner__contacts_button_box">
              {checkPartner.value.size > 0 && (
                <MuiButton
                  className="partner_delete_button"
                  disableElevation
                  onClick={handleCancelRequestPartnerShip}
                >
                  <CustomSpan fontSize={13} fontWeight={200} fontColor="#B5B7C1">
                    {'X '}
                    <T>GLOBAL_REQUEST_CANCEL</T>
                  </CustomSpan>
                </MuiButton>
              )}
              <MuiButton
                className="add_partner_button"
                variant="contained"
                disableElevation
                onClick={() => {
                  handleOpenModalPopup('newPartner');
                  detailPartnerGroupCode.setValue('');
                  majorClassify.setValue('search');
                  minorClassify.setValue('default');
                }}
              >
                <CustomSpan fontSize={13} fontWeight={400}>
                  {'+ '}
                  <T>GLOBAL_PARTNER</T>
                </CustomSpan>
              </MuiButton>
            </div>
          </div>
          <section className="partner__contacts_container">
            <div className="partner__contacts_list_wrapper">
              <div className="partner__contacts_list">
                <div className="partner__contacts_table">
                  <div className="partner__contacts_table_head">
                    <Grid container className="partner__contacts_table_row">
                      <Grid item className="contacts_table_row_item">
                        <Checkbox
                          color="primary"
                          checked={checkAll.value}
                          value={checkAll.value}
                          icon={<div className="custom_checkbox"></div>}
                          onChange={() => {
                            handleCheckAll(!checkAll.value);
                          }}
                        />
                      </Grid>
                      <Grid item className="contacts_table_row_item">
                        <CustomSpan fontSize={15} fontWeight={200} fontColor="#B5B7C1">
                          <T>GLOBAL_NUM</T>
                        </CustomSpan>
                      </Grid>
                      <Grid item className="contacts_table_row_item">
                        <T>GLOBAL_TYPE</T>
                      </Grid>
                      <Grid item className="contacts_table_row_item">
                        <T>GLOBAL_PARTNER_NAME</T>
                      </Grid>

                      <Grid item className="contacts_table_row_item">
                        <T>GLOBAL_REQUEST_DATE</T>
                      </Grid>
                    </Grid>
                  </div>

                  <div className="partner__contacts_table_body_wrapper">
                    {/* {partnerRequestStack.length < 1 && !!fetchPartnerRequestListPending && } */}
                    {!isLoadRequestSuccess && !!fetchPartnerRequestListPending && (
                      <div className="infinite-scroll-loading">
                        <CircularLoading />
                      </div>
                    )}
                    {useMemo(
                      () => (
                        <>
                          {!!isLoadRequestSuccess &&
                            (partnerRequestStack.length > 0 ? (
                              <div className="partner__contacts_table_body">
                                {partnerRequestStack.map((item: any, index: number) => {
                                  return (
                                    <Grid
                                      container
                                      className="partner__contacts_table_row"
                                      key={index}
                                      onClick={(e: any) => {
                                        handleCheckStandByPartner(item.partnershipCode);
                                      }}
                                    >
                                      <Grid item className="contacts_table_row_item">
                                        <MuiWrapper>
                                          <Checkbox
                                            checked={checkPartner?.value?.has(item.partnershipCode)}
                                            color="primary"
                                            icon={<div className="custom_checkbox"></div>}
                                          />
                                        </MuiWrapper>
                                      </Grid>
                                      <Grid item className="contacts_table_row_item">
                                        <CustomSpan fontSize={15} fontColor="#B5B7C1">
                                          {index + 1}
                                        </CustomSpan>
                                      </Grid>
                                      <Grid item className="contacts_table_row_item ">
                                        <T>
                                          {
                                            partnerTypeList.find(i => i.id === item.userTypeIdx)
                                              ?.text
                                          }
                                        </T>
                                      </Grid>
                                      <Grid container item className="contacts_table_row_item ">
                                        <Grid item className="under_line">
                                          {item.name}
                                        </Grid>
                                      </Grid>

                                      <Grid item className="contacts_table_row_item">
                                        <CustomSpan fontSize={15} fontColor="#b5b7c1">
                                          <DateConverter
                                            language={language}
                                            timestamp={item.enrollDate}
                                            format={
                                              language === 'en' ? 'MMM.DD.YYYY' : 'YYYY.MM.DD'
                                            }
                                          />
                                        </CustomSpan>
                                      </Grid>
                                    </Grid>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="partner__contacts_not_exist_partner_box">
                                <p>
                                  <T>PARTNER_SENT_REQUEST_EMPTY</T>
                                </p>
                                <MuiButton
                                  className="not_exist_add_partner_button"
                                  variant="contained"
                                  disableElevation
                                  onClick={() => {
                                    handleOpenModalPopup('newPartner');
                                    detailPartnerGroupCode.setValue('');
                                    majorClassify.setValue('search');
                                    minorClassify.setValue('default');
                                  }}
                                >
                                  <CustomSpan fontSize={16} fontWeight={700}>
                                    {'+ '}
                                    <T>GLOBAL_PARTNER</T>
                                    {' >'}
                                  </CustomSpan>
                                </MuiButton>
                                <div className="partner__contacts_not_exist_text_box">
                                  <div className="partner__contacts_not_exist_text"></div>
                                </div>
                              </div>
                            ))}
                        </>
                      ),
                      [
                        partnerRequestStack,
                        checkPartner.value,
                        checkAll.value,
                        isLoadRequestSuccess,
                      ],
                    )}
                    {!!isLoadRequestFailure && (
                      <ListEmpty
                        text="GLOBAL_LIST_ERROR"
                        isRefreshButton={true}
                        onRefresh={handleRefreshListRequset}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </Grid>
        <Grid item xs={12} className="partner__list_wrapper">
          <section>
            <h1 className="partner__content_title sr-only">
              <T>PARTNER_LIST</T>
            </h1>
            <div className="partner__list_search_options">
              <div className="partner__list_search_option_checkBox">
                {partnerTypeList.map((item, index) => {
                  if (item.id === 3) return;
                  return (
                    <FormControlLabel
                      key={index}
                      className="checkbox_control_label"
                      control={
                        <Checkbox
                          checked={checkPartnerType.value.has(item.id)}
                          onChange={() => {
                            handleCheckPartnerType(item.id);
                          }}
                          className="checkbox_control_checkbox"
                          name={item.text}
                          color="primary"
                          icon={<div className="custom_checkbox"></div>}
                        />
                      }
                      label={
                        <CustomSpan fontSize={13} fontWeight={400}>
                          <T>{item.text}</T>
                        </CustomSpan>
                      }
                    />
                  );
                })}
              </div>

              {useMemo(
                () => (
                  <MuiWrapper
                    className="partner__list_search_options_searchBar"
                    config={{
                      borderColor: 'transparent',
                      activeBorderColor: 'transparent',
                      hoverBorderColor: 'transparent',
                    }}
                  >
                    <>
                      <TextField
                        className="radius-md"
                        variant="outlined"
                        fullWidth
                        placeholder={t('PARTNER_SEARCH_PLACEHOLDER')}
                        value={keyword.value || ''}
                        onChange={keyword.onChange}
                        onKeyPress={e => e.key === 'Enter' && handleSearchPartner()}
                      />
                      <button
                        className="btn-reset partner__list_search_btn"
                        onClick={handleSearchPartner}
                      >
                        <img src={icon_magnifier} alt="search" />
                      </button>
                    </>
                  </MuiWrapper>
                ),
                [keyword.value],
              )}
            </div>
            <div className="partner__list_table">
              <div className="partner__list_head">
                <Grid container className="partner__list_row">
                  <Grid item className="partner__list_row_item">
                    <span className="sr-only">no</span>
                  </Grid>
                  <Grid item className="partner__list_row_item">
                    <span className="sr-only">partner image</span>
                  </Grid>
                  <Grid item className="partner__list_row_item">
                    <span className="sr-only">sync type</span>
                  </Grid>
                  <Grid item className="partner__list_row_item nickName">
                    <T>GLOBAL_NICKNAME</T>
                  </Grid>
                  <Grid item className="partner__list_row_item syncId">
                    <T>GLOBAL_SYNC_ID</T>
                  </Grid>
                  <Grid item className="partner__list_row_item">
                    <T>GLOBAL_TYPE</T>
                  </Grid>
                  <Grid item className="partner__list_row_item alignCenter">
                    <T>GLOBAL_GOLD</T>
                  </Grid>
                  <Grid item className="partner__list_row_item alignCenter">
                    <T>GLOBAL_UNIT_PRICE</T>
                  </Grid>
                  <Grid item className="partner__list_row_item">
                    <T>GLOBAL_MODIFIED_DATE</T>
                  </Grid>
                </Grid>
              </div>
              <div className="partner__list_body_wrapper">
                {newPartnerList.length < 1 &&
                  partnerListStack.length < 1 &&
                  !!fetchPartnerListPending &&
                  !isLoadSuccess && (
                    <div className="infinite-scroll-loading">
                      <CircularLoading />
                    </div>
                  )}
                {useMemo(
                  () => (
                    <InfiniteScroll
                      className="partner__list_body"
                      dataLength={partnerListStack?.length}
                      next={handleFetchParterList}
                      hasMore={hasMorePartnerList}
                      loader={
                        <div className="infinite-scroll-loading">
                          <CircularLoading />
                        </div>
                      }
                      height={526}
                      scrollThreshold={0.95}
                    >
                      {!!isLoadSuccess &&
                        (newPartnerList.length === 0 && partnerListStack.length === 0 ? (
                          <ListEmpty text="GLOBAL_EMPTY_LIST_PARTNER" />
                        ) : (
                          <>
                            {newPartnerList?.map((item: IPartner, index: number) => {
                              return (
                                <Grid container className="partner__list_row" key={index}>
                                  <Grid item className="partner__list_row_item new">
                                    <span className="partner__new_icon">N</span>
                                  </Grid>
                                  <Grid item className="partner__list_row_item">
                                    <div className="partner__image_box">
                                      {!!item.profileImg ? (
                                        <ImgCrop
                                          width={70}
                                          isCircle
                                          src={item.profileImg}
                                          alt="partner profile image"
                                        />
                                      ) : (
                                        <img className="partner__image" src={icon_user_circle} />
                                      )}
                                    </div>
                                  </Grid>
                                  <Grid item className="partner__list_row_item ">
                                    {item.accountType === 1 && (
                                      <div className="partner_type">Sync</div>
                                    )}
                                  </Grid>
                                  <Grid
                                    item
                                    className="partner__list_row_item under_line cursor_pointer"
                                    onClick={() => {
                                      handleOpenModalPopup('newPartner');
                                      setPrevPartnerStateProp(1);
                                      detailPartnerGroupCode.setValue(item.groupCode);
                                      majorClassify.setValue('input');
                                      minorClassify.setValue('detail');
                                    }}
                                  >
                                    {item.name}
                                  </Grid>
                                  <Grid
                                    item
                                    className="partner__list_row_item cursor_pointer"
                                    onClick={() => {
                                      handleOpenModalPopup('newPartner');
                                      setPrevPartnerStateProp(1);
                                      detailPartnerGroupCode.setValue(item.groupCode);
                                      majorClassify.setValue('input');
                                      minorClassify.setValue('detail');
                                    }}
                                  >
                                    {item.email}
                                  </Grid>
                                  <Grid item className="partner__list_row_item">
                                    <T>
                                      {partnerTypeList.find(i => i.id === item.userTypeIdx)?.text}
                                    </T>
                                  </Grid>
                                  <Grid item className="partner__list_row_item alignCenter">
                                    <MuiWrapper className="row_item_btn_wrapper">
                                      <MuiButton
                                        className="row_item_btn"
                                        variant="outlined"
                                        color="default"
                                        disableElevation
                                        onClick={() => {
                                          handleOpenModalPopup('goldStatus');
                                          detailPartnerShipCode.setValue(item.partnershipCode);
                                          detailPartnerShipIdx.setValue(item.partnershipIdx);
                                        }}
                                      >
                                        <CustomSpan fontSize={15} fontWeight={300}>
                                          <T>GLOBAL_MODIFY</T>
                                        </CustomSpan>
                                      </MuiButton>
                                    </MuiWrapper>
                                  </Grid>
                                  <Grid item className="partner__list_row_item alignCenter">
                                    <MuiWrapper className="row_item_btn_wrapper">
                                      <MuiButton
                                        className="row_item_btn"
                                        variant="outlined"
                                        color="default"
                                        disableElevation
                                        onClick={() => {
                                          handleOpenModalPopup('unitPrice');
                                          detailPartnerShipIdx.setValue(item.partnershipIdx);
                                          detailPartnerShipName.setValue(item.name);
                                          detailPartnerShipEmail.setValue(item.email);
                                        }}
                                      >
                                        <CustomSpan fontSize={15} fontWeight={300}>
                                          <T>GLOBAL_MODIFY</T>
                                        </CustomSpan>
                                      </MuiButton>
                                    </MuiWrapper>
                                  </Grid>
                                  <Grid item className="partner__list_row_item">
                                    <DateConverter
                                      language={language}
                                      timestamp={item.modifyDate}
                                      format={language === 'en' ? 'MMM.DD.YYYY' : 'YYYY.MM.DD'}
                                    />
                                  </Grid>
                                </Grid>
                              );
                            })}
                            {partnerListStack?.map((item: IPartner, index: number) => {
                              return (
                                <Grid container className="partner__list_row" key={index}>
                                  <Grid item className="partner__list_row_item">
                                    {index + 1}
                                  </Grid>

                                  <Grid item className="partner__list_row_item">
                                    <div className="partner__image_box">
                                      {!!item.profileImg ? (
                                        <ImgCrop
                                          width={70}
                                          isCircle
                                          src={item.profileImg}
                                          alt="partner profile image"
                                        />
                                      ) : (
                                        <img className="partner__image" src={icon_user_circle} />
                                      )}
                                    </div>
                                  </Grid>
                                  <Grid item className="partner__list_row_item ">
                                    {item.accountType === 1 && (
                                      <div className="partner_type">Sync</div>
                                    )}
                                  </Grid>
                                  <Grid
                                    item
                                    className="partner__list_row_item under_line cursor_pointer"
                                    onClick={() => {
                                      handleOpenModalPopup('newPartner');
                                      setPrevPartnerStateProp(1);
                                      detailPartnerGroupCode.setValue(item.groupCode);
                                      majorClassify.setValue('input');
                                      minorClassify.setValue('detail');
                                    }}
                                  >
                                    {item.name}
                                  </Grid>
                                  <Grid
                                    item
                                    className="partner__list_row_item cursor_pointer"
                                    onClick={() => {
                                      handleOpenModalPopup('newPartner');
                                      setPrevPartnerStateProp(1);
                                      detailPartnerGroupCode.setValue(item.groupCode);
                                      majorClassify.setValue('input');
                                      minorClassify.setValue('detail');
                                    }}
                                  >
                                    {item.email}
                                  </Grid>
                                  <Grid item className="partner__list_row_item">
                                    <T>
                                      {partnerTypeList.find(i => i.id === item.userTypeIdx)?.text}
                                    </T>
                                  </Grid>
                                  <Grid item className="partner__list_row_item alignCenter">
                                    <MuiWrapper className="row_item_btn_wrapper">
                                      <MuiButton
                                        className="row_item_btn"
                                        variant="outlined"
                                        color="default"
                                        disableElevation
                                        onClick={() => {
                                          handleOpenModalPopup('goldStatus');
                                          detailPartnerGroupIdx.setValue(item.userGroupIdx);
                                          detailPartnerGroupCode.setValue(item.groupCode);
                                          detailPartnerShipCode.setValue(item.partnershipCode);
                                          detailPartnerShipIdx.setValue(item.partnershipIdx);
                                        }}
                                      >
                                        <CustomSpan fontSize={15} fontWeight={300}>
                                          <T>GLOBAL_MODIFY</T>
                                        </CustomSpan>
                                      </MuiButton>
                                    </MuiWrapper>
                                  </Grid>
                                  <Grid item className="partner__list_row_item alignCenter">
                                    <MuiWrapper className="row_item_btn_wrapper">
                                      <MuiButton
                                        className="row_item_btn"
                                        variant="outlined"
                                        color="default"
                                        disableElevation
                                        onClick={() => {
                                          handleOpenModalPopup('unitPrice');
                                          detailPartnerShipIdx.setValue(item.partnershipIdx);
                                          detailPartnerShipName.setValue(item.name);
                                          detailPartnerShipEmail.setValue(item.email);
                                        }}
                                      >
                                        <CustomSpan fontSize={15} fontWeight={300}>
                                          <T>GLOBAL_MODIFY</T>
                                        </CustomSpan>
                                      </MuiButton>
                                    </MuiWrapper>
                                  </Grid>
                                  <Grid item className="partner__list_row_item">
                                    <DateConverter
                                      language={language}
                                      timestamp={item.modifyDate}
                                      format={language === 'en' ? 'MMM.DD.YYYY' : 'YYYY.MM.DD'}
                                    />
                                  </Grid>
                                </Grid>
                              );
                            })}
                          </>
                        ))}
                      {!!isLoadFailure && (
                        <ListEmpty
                          text="GLOBAL_LIST_ERROR"
                          isRefreshButton={true}
                          onRefresh={handleRefreshList}
                        />
                      )}
                    </InfiniteScroll>
                  ),
                  [partnerListStack, newPartnerList, isLoadSuccess],
                )}
              </div>
            </div>
          </section>
        </Grid>
      </Grid>
    </StyledPartner>
  );
}

const StyledPartner = styled.section<{}>`
  .under_line {
    text-decoration: underline;
  }
  .cursor_pointer {
    &:hover {
      cursor: pointer;
    }
  }
  .custom_checkbox {
    width: 20px;
    height: 20px;
    border: 1px solid #e5e7ed;
    border-radius: 4px;
  }
  .partner__container {
    justify-content: space-between;
    row-gap: 60px;
    .partner__contacts_title {
      &.contacts {
        margin-left: 23px;
      }
    }
    .partner__received_list_wrapper {
      position: relative;
      width: 524px;
      height: 465px;
      border-radius: 15px;
      box-shadow: 0px 0px 6px rgb(0 0 0 / 16%);
      padding: 20px 30px 40px;
      background-color: #fff;
      .partner__content_title {
        margin-bottom: 20px;
      }
      .partner__request_list_wrapper {
        width: 100%;
        padding: 0 27px;
        height: 350px;
        margin-top: 35px;
      }
      .received_partner_is_empty_wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 100%;
        padding: 0 27px;
        height: 350px;
        margin-top: 35px;
        .received_partner_is_empty_image {
        }
        .received_partner_is_empty_text {
          margin-top: 10px;
          text-align: center;
          color: #b5b7c1;
          line-height: 29px;
          font-size: 19px;
          font-style: italic;
        }
      }
    }
    .partner__contacts_header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      .partner__contacts_button_box {
        display: flex;
        .partner_delete_button {
          display: inline-flex;
          justify-content: center;
          align-items: center;
          width: 178px;
          height: 40px;
          border-radius: 20px;
        }
        .add_partner_button {
          width: 178px;
          height: 40px;
          border-radius: 20px;
          background-color: #00a4e3;
          box-shadow: inset 3px 3px 6px rgb(0 0 0 / 16%);
        }
      }
    }
    .partner__contacts_container {
      width: 1048px;
      height: 465px;
      border-radius: 15px;
      box-shadow: 0px 0px 6px rgb(0 0 0 / 16%);
      padding: 20px 20px 0 12px;
      background-color: #fff;
      .partner__contacts_list_wrapper {
        position: relative;
        height: 450px;

        .partner__contacts_list {
          position: absolute;
          top: 0;
          left: 0;
          z-index: 10;
          width: 100%;
          background-color: #ffffff;
          .partner__contacts_table {
            height: 100%;
            .partner__contacts_table_head {
              width: 100%;
              height: 40px;
              border-radius: 5px;
              background-color: #edf4fb;
              .partner__contacts_table_row {
                font-weight: 500;
                height: 40px;
                font-size: 15px;
              }
            }
            .partner__contacts_table_body_wrapper {
              overflow-y: overlay;
              background: white;
              padding-right: 16px;
              margin-right: -16px;
              height: 390px;

              position: relative;
              .partner__contacts_not_exist_partner_box {
                width: 550px;
                width: 100%;
                height: 100%;
                position: relative;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                row-gap: 25px;
                p {
                  font-size: 18px;
                  color: #888;
                }
                .not_exist_add_partner_button {
                  width: 214px;
                  height: 40px;
                  border-radius: 20px;
                  background-color: ${color.navy_blue};
                }
              }
              .partner__contacts_table_body {
                font-size: 16px;
                width: 100%;
                height: 100%;
                .partner__contacts_table_row {
                  height: 58px;
                  border-bottom: 1px solid #edf4fb;
                  &:last-child {
                    border-bottom: none;
                  }
                }
              }
            }
            .partner__contacts_table_row {
              width: 100%;
              align-items: center;
              .contacts_table_row_item {
                font-size: 15px;
                font-weight: 400;
                &:nth-child(1) {
                  font-weight: 200;
                  width: 6%;
                  text-align: center;
                }
                &:nth-child(2) {
                  width: 7%;
                }
                &:nth-child(3) {
                  width: 16%;
                }

                &:nth-child(4) {
                  width: 54%;
                  justify-content: space-between;
                  .request_status {
                    color: #b5b7c1;
                    display: flex;
                    align-items: center;
                    margin-right: 50px;
                    .request_status_icon {
                      margin-right: 10px;
                    }
                  }
                }
                &:nth-child(5) {
                  width: 17%;
                }
              }
            }
          }
        }
      }
    }
    .partner__list_wrapper {
      padding-top: 40px;
      padding-bottom: 120px;
      border-top: 5px solid #f4f5fa;
      width: 100%;
      .partner__list_search_options {
        display: flex;
        align-items: center;
        column-gap: 20px;
        .partner__list_search_option_checkBox {
          border-radius: 5px;
          display: flex;
          align-items: center;
          padding: 0 30px;
          background-color: #f4f5fa;
          .checkbox_control_label {
            margin-right: 20px;
            &:last-child {
              margin-right: 0;
            }
            .checkbox_control_checkbox {
              width: 42px;
              height: 42px;
            }
          }
        }
        .partner__list_search_options_datePicker {
          width: 272px;
        }
        .partner__list_search_options_searchBar {
          width: 550px;
          height: 40px;
          position: relative;
          .MuiInputBase-root {
            background-color: #f4f5fa;
          }
          .partner__list_search_btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            position: absolute;
            right: 0;
            top: 0;
            width: 40px;
            height: 40px;
            border-top-right-radius: 5px;
            border-bottom-right-radius: 5px;
            background-color: ${color.navy_blue};
          }
        }
      }
      .partner__list_table {
        margin-top: 25px;
        width: 100%;

        .partner__list_head {
          border-radius: 10px 10px 0 0;
          box-shadow: inset 0 3px 3px rgb(0 0 0 / 16%);
          padding-bottom: 10px;
          margin-bottom: -10px;
          background-color: #00155e;

          .partner__list_row {
            height: 58px;
            .partner__list_row_item {
              font-size: 16px;
              color: #ffffff;
              font-weight: 400;
            }
          }
        }
        .partner__list_body_wrapper {
          background-color: #ffffff;
          padding: 17px 0;
          box-shadow: 0 0 10px rgb(0 0 0 / 16%);
          border-radius: 10px;
          height: 560px;
          position: relative;
          .partner__list_body {
            height: 526px;
            overflow-y: overlay;
          }
          .partner__list_row {
            border-bottom: 1px solid #e7e8f2;
            padding: 20px 0;
            position: relative;
            &:nth-child(1) {
              padding: 5px 0 20px 0;
            }
            &:last-child {
              padding: 20px 0 5px 0;
              border-bottom: none;
            }
            .partner__list_row_item {
              font-size: 16px;
              color: #303030;
              font-weight: 400;
            }
          }
        }

        .partner__list_row {
          align-items: center;

          .partner__list_row_item {
            &.new {
              .partner__new_icon {
                width: 20px;
                height: 20px;
                border-radius: 10px;
                background-color: #33b5e4;
                color: #ffffff;

                display: flex;
                align-items: center;
                justify-content: center;
                font-weight: 500;
                font-size: 13px;
              }
            }
            display: flex;
            align-items: center;
            &.alignCenter {
              justify-content: center;
            }
            .partner_type {
              width: 45px;
              height: 18px;
              display: flex;
              align-items: center;
              justify-content: center;
              background-color: #00a4e3;
              border-radius: 3px;
              font-size: 11px;
              color: #fff;
            }
            .partner__image_box {
              border-radius: 50%;
              box-shadow: 0px 0px 6px rgb(0 0 0 / 16%);
              .partner__image {
                width: 70px;
                height: 70px;
              }
            }
            .row_item_btn_wrapper {
              display: flex;
              justify-content: center;
              align-items: center;
              .row_item_btn {
                width: 140px;
                height: 30px;
                border-radius: 18px;
                &:hover {
                  background-color: #ffffff;
                  border: 1px solid ${color.blue};
                  color: ${color.blue};
                }
              }
            }
            &:nth-child(1) {
              width: 80px;
              padding-left: 50px;
              justify-content: center;
            }
            &:nth-child(2) {
              width: 100px;
              justify-content: center;
            }
            &:nth-child(3) {
              width: 80px;
              justify-content: center;
            }
            &:nth-child(4) {
              width: 210px;
            }
            &:nth-child(5) {
              width: 310px;
            }
            &:nth-child(6) {
              width: 260px;
            }
            &:nth-child(7) {
              width: 200px;
            }
            &:nth-child(8) {
              width: 200px;
            }
            &:nth-child(9) {
              width: 180px;
            }
          }
        }
      }
    }
  }
`;

export default React.memo(PartnerContainer);
